import { useQuery } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'

import { LONG_LIVED_IMAGE_CACHE_CONTROL } from '../../../../constants/storage.constants'
import { updateGeneralConfiguration, uploadImage } from '../../services/configuration.service'
import { QueryKeys } from '../../../../constants/queryKeys.constants'
import { DEFAULT_BANNER_POSITION, GeneralConfigurationType, PlanLevel } from '../types/types'
import { getGeneralConfiguration } from '../../services/configuration.service'
import { ConfigurationCategory } from '../../types/configuration.types'

const getOriginalFileName = (url: string) => {
  if (!url) return ''
  const decodedUrl = decodeURIComponent(url)
  const fileName = decodedUrl.split('/').pop()?.split('?')[0] || ''
  return fileName.replace(/-(\d+)\./, '.')
}

export const useGeneralConfiguration = () => {
  const queryClient = useQueryClient()

  const { data: configuration, isLoading } = useQuery({
    queryKey: [QueryKeys.CONFIGURATION, ConfigurationCategory.GENERAL],
    queryFn: getGeneralConfiguration,
  })

  const update = async (data: Partial<GeneralConfigurationType>) => {
    await updateGeneralConfiguration(data)
    queryClient.invalidateQueries({ queryKey: [QueryKeys.CONFIGURATION, ConfigurationCategory.GENERAL] })
  }

  const uploadLogo = async (file: File) => {
    const imageUrl = await uploadImage(file, ConfigurationCategory.GENERAL)
    await update({ logo: imageUrl })
  }

  const uploadBanner = async (file: File) => {
    const imageUrl = await uploadImage(file, ConfigurationCategory.GENERAL, { cacheControl: LONG_LIVED_IMAGE_CACHE_CONTROL })
    await update({ banner: imageUrl })
  }

  return {
    logoFilename: getOriginalFileName(configuration?.logo || ''),
    bannerFilename: getOriginalFileName(configuration?.banner || ''),
    bannerTitle: configuration?.bannerTitle || '',
    bannerSubtitle: configuration?.bannerSubtitle || '',
    bannerPosition: configuration?.bannerPosition || DEFAULT_BANNER_POSITION,
    infoTitle: configuration?.infoTitle || '',
    infoText: configuration?.infoText || '',
    instaUrl: configuration?.instaUrl || '',
    pricingPlan: configuration?.pricingPlan || PlanLevel.Starter,
    facebookUrl: configuration?.facebookUrl || '',
    websiteUrl: configuration?.websiteUrl || '',
    contactPhone: configuration?.contactPhone || '',
    contact: configuration?.contact || '',
    pricelist: configuration?.pricelist || '',
    services: configuration?.services || '',
    reservationPolicy: configuration?.reservationPolicy || '',
    privacyPolicy: configuration?.privacyPolicy || '',
    logo: configuration?.logo || '',
    banner: configuration?.banner || '',
    isLoading,
    update,
    uploadLogo,
    uploadBanner,
  }
}
